import { createContext, useContext, useEffect, useState } from "react";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase/firebaseConfig";
import {
  collection,
  collectionGroup,
  getDocs,
  deleteDoc,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  addDoc
} from "firebase/firestore";
import { toast } from "react-toastify";

const AdminContext = createContext();

export const AdminProvider = ({ children }) => {
  const [admin, setAdmin] = useState(localStorage.getItem("admin_uid"));
  const [allComments, setAllComments] = useState([]);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const selectBlog = (blog) => setSelectedBlog(blog);

  useEffect(() => {
    const verifyAdminFromStorage = async () => {
      const storedUID = localStorage.getItem("admin_uid");
      if (!storedUID) return;

      try {
        const adminRef = doc(db, "admin", storedUID);
        const adminDoc = await getDoc(adminRef);

        if (adminDoc.exists() && adminDoc.data().role === "all") {
          setAdmin(adminDoc.data().email);
        } else {
          localStorage.removeItem("admin_uid");
          setAdmin(null);
        }
      } catch (error) {
        console.error("Failed to verify admin:", error);
        localStorage.removeItem("admin_uid");
        setAdmin(null);
      }
    };

    verifyAdminFromStorage();
  }, []);

  // 🔐 Admin Auth
  const AdminLogin = async (email, password, navigate) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const uid = userCredential.user.uid;
      const adminRef = doc(db, "admin", uid);
      const adminDoc = await getDoc(adminRef);

      if (!adminDoc.exists() || adminDoc.data().role !== "all") {
        throw new Error("Access denied");
      }

      toast.success("Login successful");
      localStorage.setItem("admin_uid", uid);
      setAdmin(email);
      navigate("/admin/dashboard");
    } catch (err) {
      console.error("Admin login error:", err.message);
      toast.error(err.message);
      throw err;
    }
  };

  const AdminLogout = () => {
    setAdmin(null);
    localStorage.removeItem("admin_uid");
    toast.success("Logged out successfully");
  };

  // 👤 User Management
  const fetchUsers = async () => {
    const snapshot = await getDocs(collection(db, "users"));
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  };

  const deleteUser = async (userId) => {
    await deleteDoc(doc(db, "users", userId));
    toast.success("User deleted successfully");
  };

  // ✉️ Contact Messages
  const fetchMessages = async () => {
    const snapshot = await getDocs(collection(db, "contacts"));
    return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
  };

  // Recipe Management
  const addRecipe = async (recipeData) => {
  try {
    const ref = await addDoc(collection(db, '_recipes'), recipeData);
    toast.success('Recipe saved!');
    return ref.id;
  } catch (err) {
    console.error("Error adding recipe:", err.message);
    throw err;
  }
};

const fetchRecipes = async () => {
  const snapshot = await getDocs(collection(db, '_recipes'));
  return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};



  // 📚 Blog Management (no categories)
  const fetchBlogs = async () => {
    const snapshot = await getDocs(collection(db, '_blogs'));
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  };

 const addBlog = async (blogData) => {
  try {
    const blogRef = await addDoc(collection(db, '_blogs'), blogData);
    toast.success("Blog added successfully!");
    return blogRef.id;
  } catch (error) {
    console.error("Error adding blog:", error.message);
    throw error;
  }
};


  const deleteBlog = async (blogId) => {
    try {
      await deleteDoc(doc(db, '_blogs', blogId));
      toast.success("Blog deleted");
    } catch (error) {
      console.error("Delete error:", error.message);
      throw error;
    }
  };

  const updateBlog = async (blogId, updatedData) => {
    try {
      const ref = doc(db, '_blogs', blogId);
      await updateDoc(ref, {
        ...updatedData,
        updatedAt: new Date(),
      });
      toast.success("Blog updated successfully");
    } catch (error) {
      console.error("Update error:", error.message);
      throw error;
    }
  };

  // 💬 Comment Management
  const fetchComments = async () => {
    try {
      const snapshot = await getDocs(collectionGroup(db, "comments"));
      const data = snapshot.docs.map((doc) => {
        const d = doc.data();
        return {
          id: doc.id,
          ...d,
          path: doc.ref.path,
          approved: typeof d.approved === "string" ? d.approved === "true" : !!d.approved,
        };
      });
      setAllComments(data);
      return data;
    } catch (error) {
      console.error("Error fetching comments:", error.message);
      return [];
    }
  };


  const updateCommentApproval = async (commentId, path, currentStatus) => {
    try {
      const commentRef = doc(db, path);
      await updateDoc(commentRef, {
        approved: !currentStatus,
      });

      setAllComments((prev) =>
        prev.map((comment) =>
          comment.id === commentId ? { ...comment, approved: !currentStatus } : comment
        )
      );

      toast.success(`Comment ${!currentStatus ? "approved" : "disapproved"}`);
    } catch (err) {
      console.error("Error updating comment approval:", err.message);
      toast.error("Failed to update comment status");
    }
  };

  // 🔄 Popup toggle state
const [popupEnabled, setPopupEnabled] = useState(false);

const fetchPopupStatus = async () => {
  try {
    // toast.info("Getting Pop Up Info");
    const ref = doc(db, '_function', 'popupControl');
    const snap = await getDoc(ref);
    if (snap.exists()) {
      setPopupEnabled(snap.data().enabled);
    }
  } catch (err) {
    console.error("Failed to fetch popup status:", err.message);
  }
};

const togglePopupStatus = async (status) => {
  try {
    const ref = doc(db, '_function', 'popupControl');
    await setDoc(ref, { enabled: status }, { merge: true });
    setPopupEnabled(status);
    toast.success(`Popup turned ${status ? "on" : "off"}`);
  } catch (err) {
    console.error("Failed to update popup status:", err.message);
    toast.error("Error updating popup setting");
  }
};


  return (
    <AdminContext.Provider
      value={{
        admin,
        setAdmin,
        AdminLogin,
        AdminLogout,
        fetchUsers,
        deleteUser,
        fetchMessages,
        fetchBlogs,
        addBlog,
        deleteBlog,
        updateBlog,
        selectedBlog,
        selectBlog,
        fetchComments,
        addRecipe,
        updateCommentApproval,
        allComments,
        fetchRecipes,
        fetchPopupStatus,
        togglePopupStatus,
          popupEnabled,
          setPopupEnabled
      }}
    >
      {children}
    </AdminContext.Provider>
  );
};

export const useAdminContext = () => useContext(AdminContext);
